import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Lightbulb, 
  Send, 
  Loader2, 
  CheckCircle,
  Globe,
  Building2,
  Mail,
  MessageSquare,
  ArrowLeft,
  UserCheck,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuggestPage() {
  const [searchParams] = useSearchParams();
  const isVendorApplication = searchParams.get('type') === 'vendor';
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    platform_name: '',
    company_name: '',
    website_url: '',
    reason: '',
    contact_email: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  // Auto-prepend https:// to website URL if missing protocol
  const handleWebsiteBlur = (e) => {
    const value = e.target.value.trim();
    if (value && !value.match(/^https?:\/\//i)) {
      // If it starts with www. or looks like a domain, prepend https://
      if (value.match(/^(www\.)?[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}/)) {
        setFormData(prev => ({ ...prev, website_url: `https://${value}` }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          is_vendor_application: isVendorApplication
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md text-center">
            <CardContent className="py-12">
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
              <h1 className="text-2xl font-semibold text-foreground mb-3">
                {isVendorApplication ? 'Application Received' : 'Thank You!'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {isVendorApplication 
                  ? 'Your vendor application has been submitted. Our team will review it and contact you within 2-3 business days.'
                  : 'Your platform suggestion has been submitted. We review all suggestions and may reach out if we need more information.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" asChild>
                  <Link to="/">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </Link>
                </Button>
                {isVendorApplication && (
                  <Button variant="outline" asChild>
                    <Link to="/vendors">
                      Learn more about Vendor Portal
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 py-12 px-4">
        <div className="max-w-lg mx-auto">
          {/* Breadcrumb */}
          <Link 
            to={isVendorApplication ? '/vendors' : '/'}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {isVendorApplication ? 'Back to Vendor Info' : 'Back to Home'}
          </Link>

          <Card>
            <CardHeader className="text-center pb-2">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                {isVendorApplication ? (
                  <UserCheck className="w-6 h-6 text-primary" />
                ) : (
                  <Lightbulb className="w-6 h-6 text-primary" />
                )}
              </div>
              <CardTitle className="text-xl">
                {isVendorApplication 
                  ? 'Apply to Claim Your Platform' 
                  : 'Suggest a Platform'
                }
              </CardTitle>
              <CardDescription className="text-base">
                {isVendorApplication 
                  ? 'Submit your application to manage your platform profile on GoCompareAI.'
                  : 'Know an AI platform that should be on GoCompareAI? Let us know.'
                }
              </CardDescription>
            </CardHeader>

            <CardContent>
              {/* Vendor-specific notice */}
              {isVendorApplication && (
                <div className="bg-muted/50 rounded-lg p-4 mb-6 flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Vendor applications are reviewed</p>
                    <p>We verify all applications to ensure legitimacy. Only authorized representatives can claim platform profiles.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="platform_name" className="flex items-center gap-2">
                    <Lightbulb className="w-3.5 h-3.5" />
                    {isVendorApplication ? 'Platform Name *' : 'Platform Name *'}
                  </Label>
                  <Input
                    id="platform_name"
                    name="platform_name"
                    placeholder={isVendorApplication ? 'Your platform name as listed on GoCompareAI' : 'e.g., SuperAI Assistant'}
                    value={formData.platform_name}
                    onChange={handleChange}
                    required
                    data-testid="suggest-platform-name"
                  />
                  {isVendorApplication && (
                    <p className="text-xs text-muted-foreground">
                      If your platform is not yet listed, we will add it during the review process.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name" className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5" />
                    Company Name *
                  </Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    placeholder="e.g., SuperAI Inc."
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    data-testid="suggest-company-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website_url" className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5" />
                    {isVendorApplication ? 'Official Website *' : 'Website URL'}
                  </Label>
                  <Input
                    id="website_url"
                    name="website_url"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.website_url}
                    onChange={handleChange}
                    onBlur={handleWebsiteBlur}
                    required={isVendorApplication}
                    data-testid="suggest-website"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter your website URL (we'll add https:// if needed)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason" className="flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5" />
                    {isVendorApplication ? 'Your role at the company *' : 'Why should we add it? *'}
                  </Label>
                  <Textarea
                    id="reason"
                    name="reason"
                    placeholder={isVendorApplication 
                      ? 'Describe your role and why you are authorized to manage this platform profile...'
                      : 'Tell us what makes this platform noteworthy...'
                    }
                    value={formData.reason}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="suggest-reason"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" />
                    {isVendorApplication ? 'Business Email *' : 'Your Email *'}
                  </Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    placeholder={isVendorApplication ? 'you@company.com' : 'you@example.com'}
                    value={formData.contact_email}
                    onChange={handleChange}
                    required
                    data-testid="suggest-email"
                  />
                  <p className="text-xs text-muted-foreground">
                    {isVendorApplication 
                      ? 'Please use your company email address for verification purposes.'
                      : 'We may contact you if we need more details.'
                    }
                  </p>
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-11 gap-2"
                  data-testid="suggest-submit-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isSubmitting 
                    ? 'Submitting...' 
                    : isVendorApplication 
                      ? 'Submit Application' 
                      : 'Submit Suggestion'
                  }
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
